/**
 * Tool catalogue shown on the showcase page. Mirrors the registrations in
 * server_node/src/handlers/mcpHandlers.ts — keep the two in sync when a tool
 * is added, renamed or gains a widget.
 */

export interface ToolEntry {
    /** MCP tool name — what the model actually calls. */
    name: string;
    /** Human title from the tool registration. */
    title: string;
    /** One line, plain language. */
    summary: string;
    /** Widget id rendered inline in the chat, if the tool has one. */
    widget?: string;
    /** True for the two tools that change something upstream. */
    write?: boolean;
}

export interface ToolGroup {
    /** Two-letter "sector" code shown on the board. */
    code: string;
    label: string;
    blurb: string;
    tools: ToolEntry[];
}

export const TOOL_GROUPS: ToolGroup[] = [
    {
        code: 'FL',
        label: 'Flights & status',
        blurb: 'Live operations and bookable inventory.',
        tools: [
            { name: 'flight-status', title: 'Check Flight Status by Number', summary: 'Departure, arrival, gate, terminal and delay for one flight.', widget: 'flight-status' },
            { name: 'flight-status-by-route', title: 'Check Flight Status by Route', summary: 'Every scheduled flight between two airports on a date, with live status.', widget: 'flight-status' },
            { name: 'search_flights', title: 'Search Flights', summary: 'One-way, round-trip and multi-city availability with fares.', widget: 'flight-availability' },
            { name: 'search_inbound_flights', title: 'Search Inbound Flights', summary: 'Return options priced against a chosen outbound.', widget: 'flight-availability' },
            { name: 'find_flight_price_calendar', title: 'Find Cheapest Flight Dates', summary: 'Lowest fare per day across a month for a route.', widget: 'price-calendar' }
        ]
    },
    {
        code: 'BK',
        label: 'Bookings & baggage',
        blurb: 'Everything tied to a PNR.',
        tools: [
            { name: 'get_booking_details', title: 'Get Booking Details', summary: 'Passengers, segments, ticket numbers and status for a reservation.', widget: 'booking-details' },
            { name: 'get_booking_baggage_allowance', title: 'Get Booking Baggage Allowance', summary: 'Checked and cabin allowance plus extra-bag prices for a booking.', widget: 'baggage' },
            { name: 'manage_booking_link', title: 'Manage Booking / Web Check-in Link', summary: 'Deep link straight into manage-booking or online check-in.', widget: 'info-card' },
            { name: 'pnr_finder', title: 'Recover Lost PNR', summary: 'Finds a booking reference from ticket and passenger details.' },
            { name: 'get_baggage_case_details', title: 'Get Baggage Case Status', summary: 'Where a delayed or damaged bag case stands right now.' },
            { name: 'add_message_to_baggage_case', title: 'Add Message to Baggage Case', summary: 'Append a note to an open baggage case.', write: true },
            { name: 'generate_baggage_issue_link', title: 'Generate Baggage Issue Form Link', summary: 'Pre-filled link to report a baggage problem.', widget: 'info-card' }
        ]
    },
    {
        code: 'OF',
        label: 'Offers & upgrades',
        blurb: 'Promotions, Business upgrades and the Istanbul stopover.',
        tools: [
            { name: 'get_airline_promotions', title: 'Get Airline Promotions', summary: 'Current campaigns filtered by country and airports.', widget: 'promotions' },
            { name: 'get_business_upgrade_offers', title: 'Business Upgrade Offers for a Booking', summary: 'Upgrade offers available on each segment of a booking.', widget: 'business-upgrade' },
            { name: 'create_business_upgrade_payment_link', title: 'Business Upgrade Payment Link', summary: 'Secure payment link for a chosen upgrade offer.', widget: 'info-card', write: true },
            { name: 'check_stopover_eligibility', title: 'Check Stopover in Istanbul Eligibility', summary: 'Whether a booking qualifies for a complimentary Istanbul stay.', widget: 'stopover' },
            { name: 'check_stopover_routes', title: 'Check Stopover in Istanbul Routes', summary: 'Which routes and hotel nights the stopover programme covers.', widget: 'stopover' }
        ]
    },
    {
        code: 'TR',
        label: 'Travel planning',
        blurb: 'Destinations and holiday packages.',
        tools: [
            { name: 'get_city_guide', title: 'Get City Guide', summary: 'What to see, how to move around, weather and local tips.', widget: 'city-guide' },
            { name: 'search_hotel_packages', title: 'Search Turkish Airlines Holiday Packages', summary: 'Flight + hotel bundles for a route and dates.', widget: 'hotel-packages' },
            { name: 'generate_holidays_search_link', title: 'Flight + Hotel Packages Link', summary: 'Hand-off link into Turkish Airlines Holidays search.', widget: 'info-card' }
        ]
    },
    {
        code: 'SP',
        label: 'Support',
        blurb: 'Answers without a phone call.',
        tools: [
            { name: 'get_contact_info', title: 'Get Turkish Airlines Contact Info', summary: 'Call-centre numbers and sales offices for a country or city.' },
            { name: 'search_thy_faq', title: 'Search Turkish Airlines FAQ', summary: 'Official answers on fares, baggage, check-in, Miles&Smiles and more.' },
            { name: 'get_feedback_status', title: 'Get Feedback / Complaint Status', summary: 'Progress of a submitted feedback or complaint case.' }
        ]
    }
];

export const ALL_TOOLS = TOOL_GROUPS.flatMap((g) => g.tools);
export const TOOL_COUNT = ALL_TOOLS.length;
export const WIDGET_COUNT = new Set(ALL_TOOLS.map((t) => t.widget).filter(Boolean)).size;
export const WRITE_COUNT = ALL_TOOLS.filter((t) => t.write).length;

export const EXAMPLE_PROMPTS: Array<{ prompt: string; tool: string }> = [
    { prompt: 'Is TK1 to New York on time today?', tool: 'flight-status' },
    { prompt: 'Cheapest week in October to fly Istanbul → Tokyo?', tool: 'find_flight_price_calendar' },
    { prompt: 'Show my booking ABC123, surname Yilmaz, and how many bags I can check.', tool: 'get_booking_details' },
    { prompt: 'Can I upgrade my London flight to Business?', tool: 'get_business_upgrade_offers' },
    { prompt: 'Do I qualify for the free Istanbul stopover on this ticket?', tool: 'check_stopover_eligibility' },
    { prompt: 'Plan me two days in Cappadocia before my flight home.', tool: 'get_city_guide' }
];
